<template>
    <div class="create-edit-warehouse">
        <div class="create-edit-warehouse__background">
            <img src="~@/assets/images/dashboard-admin.png" alt="kasanowa" />
        </div>
        <KLayoutWrapper>
            <template #default>
                <div>
                    <div class="create-edit-warehouse__wrapper">
                        <div class="create-edit-warehouse__title">{{ router.currentRoute.value.params.id === 'create' ? $t('warehouse.createWarehouse') : $t('warehouse.editWarehouse') }}</div>
                        <div class="create-edit-warehouse__close-wrapper kasanowa-hover" @click="navigateTo(!isAdminWholesaler ? '/dashboard' : `/warehouse/details/${userInfo.wholesaler_ids[0]}`)">
                            <KIcon icon="close" :color="black" width="25" height="25" />
                        </div>
                        <KLoader v-show="loadingAdmins || loadingWarehouseDetails" />
                        <Form v-show="!loadingAdmins && !loadingWarehouseDetails" :validation-schema="schema" @submit="onSubmit" class="create-edit-warehouse__form-wrapper">
                            <div class="create-edit-warehouse__input-wrapper">
                                <KTextField
                                    id="input-id-asseco"
                                    v-model="warehouseForm.idAsseco"
                                    name="idAsseco"
                                    label=""
                                    :placeholder="$t('warehouse.idAssecoForm')"
                                />
                                <KTextField
                                    id="input-name"
                                    v-model="warehouseForm.name"
                                    name="name"
                                    label=""
                                    :placeholder="$t('warehouse.nameForm')"
                                />
                            </div>
                            <div class="create-edit-warehouse__input-wrapper">
                                <KTextField
                                    id="input-address"
                                    v-model="warehouseForm.address"
                                    name="address"
                                    label=""
                                    :placeholder="$t('warehouse.addressForm')"
                                />
                            </div>
                            <div class="create-edit-warehouse__autocomplete-wrapper">
                                <KAutocomplete
                                    id="select-warehouse-list"
                                    v-model="warehouseForm.adminsIds"
                                    name="adminsIds"
                                    :get-items="adminListItems"
                                    item-title="email"
                                    item-value="id"
                                    label=""
                                    :placeholder="$t('warehouse.adminsForm')"
                                    variant="outlined"
                                    multiple
                                    clearable
                                    :disabled="isAdminWholesaler"
                                />
                            </div>
                            <div class="create-edit-warehouse__btn-wrapper">
                                <KButton
                                    id="submit-btn"
                                    :label="$t('warehouse.save')"
                                    type="submit"
                                    icon="arrowRight"
                                    :color="black"
                                    width="11"
                                    height="11"
                                    :disabled="loading"
                                    :loading="loading"
                                />
                            </div>
                        </Form>
                    </div>
                </div>
            </template>
        </KLayoutWrapper>
    </div>
</template>

<script setup lang="ts">
import { ref } from '@vue/runtime-core';
import router from '@/router';
import { Form } from 'vee-validate';
import * as yup from 'yup';
import WarehouseApi from '@/api/v1/WarehouseApi';
import UserApi from '@/api/v1/UserApi';
import { toast } from 'vue3-toastify';
import { navigateTo, navigateToWithSuccessToast, navigateToWithErrorToast } from '@/hooks/NavigateHooks';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/user';
import variables from '@/assets/scss/modules/variables.module.scss';
import type { IUser } from '@/models/UserModel';
import type { IWarehouseForm } from '@/models/WarehouseModel';
import KLayoutWrapper from '@/components/atoms/KLayoutWrapper.vue';
import KTextField from '@/components/atoms/KTextField.vue';
import KButton from '@/components/atoms/KButton.vue';
import KAutocomplete from '@/components/atoms/KAutocomplete.vue';
import KLoader from '@/components/atoms/KLoader.vue';
import KIcon from '@/components/atoms/KIcon.vue';

const { isAdminWholesaler, userInfo } = storeToRefs(useUserStore());
const { black } = variables;

const userApi = new UserApi();
const loadingAdmins = ref<boolean>(false);
const adminListItems = ref<IUser[]>([]);
const adminsList = async() => {
    loadingAdmins.value = true;
    try {
        const result = await userApi.adminList();
        if (!result.isSuccess) {
            toast.error(t('warehouse.toast.errorAdminsList'));
            loadingAdmins.value = false;
            return;
        }
        adminListItems.value = result.payload.data;
    } catch (e) {
        toast.error(t('warehouse.toast.errorAdminsList'));
        loadingAdmins.value = false;
        return;
    }
    loadingAdmins.value = false;
};
adminsList();

const warehouseForm = ref<IWarehouseForm>({
    idAsseco: null,
    name: null,
    address: null,
    adminsIds: null,
});
const schema:any = yup.object({
    idAsseco: yup.string().required(`${t('warehouse.validation.idAssecoRequired')}`).label('idAsseco'),
    name: yup.string().required(`${t('warehouse.validation.nameRequired')}`).label('name'),
    address: yup.string().required(`${t('warehouse.validation.addressRequired')}`).label('address'),
});
const loading = ref<boolean>(false);
const warehouseApi = new WarehouseApi();
const onSubmit = async() => {
    loading.value = true;
    if (router.currentRoute.value.params.id === 'create') {
        try {
            const result = await warehouseApi.createWarehouse({
                assecoId: warehouseForm.value.idAsseco,
                name: warehouseForm.value.name,
                address: warehouseForm.value.address,
                admins_ids: warehouseForm.value.adminsIds,
            });
            if (!result.isSuccess) {
                toast.error(t('warehouse.toast.errorCreateWarehouse'));
                loading.value = false;
                return;
            }
            navigateToWithSuccessToast(!isAdminWholesaler.value ? '/dashboard' : `/warehouse/details/${userInfo.value.wholesaler_ids[0]}`, t('warehouse.toast.successCreateWarehouse'));
        } catch (e) {
            toast.error(t('warehouse.toast.errorCreateWarehouse'));
            loading.value = false;
            return;
        }
    } else {
        try {
            const result = await warehouseApi.editWarehouse(
                {
                    assecoId: warehouseForm.value.idAsseco,
                    name: warehouseForm.value.name,
                    address: warehouseForm.value.address,
                    admins_ids: warehouseForm.value.adminsIds,
                },
                router.currentRoute.value.params.id,
            );
            if (!result.isSuccess) {
                toast.error(t('warehouse.toast.errorEditWarehouse'));
                loading.value = false;
                return;
            }
            navigateToWithSuccessToast(!isAdminWholesaler.value ? '/dashboard' : `/warehouse/details/${userInfo.value.wholesaler_ids[0]}`, t('warehouse.toast.successEditWarehouse'));
        } catch (e) {
            toast.error(t('warehouse.toast.errorEditWarehouse'));
            loading.value = false;
            return;
        }
    }
    loading.value = false;
};

const loadingWarehouseDetails = ref<boolean>(false);
const salesRepDetails = async() => {
    loadingWarehouseDetails.value = true;
    try {
        const result = await warehouseApi.warehouseDetails(router.currentRoute.value.params.id);
        if (!result.isSuccess) {
            navigateToWithErrorToast('/dashboard', t('warehouse.toast.errorWarehouseDetails'))
            loadingWarehouseDetails.value = false;
            return;
        }
        warehouseForm.value.idAsseco = result.payload.asseco_id;
        warehouseForm.value.name = result.payload.name;
        warehouseForm.value.address = result.payload.address;
        warehouseForm.value.adminsIds = result.payload.admins_ids;
    } catch (e) {
        navigateToWithErrorToast('/dashboard', t('warehouse.toast.errorWarehouseDetails'))
        loadingWarehouseDetails.value = false;
        return;
    }
    loadingWarehouseDetails.value = false;
};
if (router.currentRoute.value.params.id !== 'create') {
    salesRepDetails();
}
</script>

<style scoped lang="scss">
.create-edit-warehouse {
    position: relative;
    width: 100%;

    &__background {
        position: absolute;
        top: 0;
        left: 0;
        z-index: 0;
        width: 100%;
        height: 100%;

        img {
            object-fit: cover;
            width: 100%;
            height: calc(100% - 184px);
            min-height: calc(100vh - 184px);

            @include media-breakpoint-up(lg) {
                height: calc(100% - 217px);
                min-height: calc(100vh - 217px);
            }
        }
    }

    &__wrapper {
        position: relative;
        background: rgba($white, 0.15);
        backdrop-filter: blur(30px) saturate(150%);
        -webkit-backdrop-filter: blur(30px) saturate(150%);
        border-radius: 5px;
        border: 1px solid $secondary-400;
        padding: 45px 15px 15px 15px;
        max-width: 1025px;
        margin: 20px auto 0;

        @include media-breakpoint-up(lg) {
            padding: 124px 52px 60px 52px;
            margin: 50px auto 0;
        }
    }

    &__title {
        width: 90%;
        position: absolute;
        top: -22px;
        left: 50%;
        transform: translateX(-50%);
        background-color: $primary-400;
        text-transform: uppercase;
        font-weight: 900;
        letter-spacing: 0;
        text-align: center;
        padding: 10px;

        @include media-breakpoint-up(lg) {
            font-size: 52px;
            padding: 9px;
            top: -50px;
        }
    }

    &__form-wrapper {
        display: flex;
        flex-direction: column;
    }

    &__input-wrapper {
        display: flex;
        flex-direction: column;

        @include media-breakpoint-up(lg) {
            flex-direction: row;
            gap: 16px;
        }

        &:deep(.k-input .v-field__input) {
            background-color: $white;
        }

        &:deep(input::placeholder) {
            opacity: 0.9;
        }
    }

    &__autocomplete-wrapper {
        &:deep(.k-autocomplete .v-field) {
            background-color: $white;
        }

        &:deep(input::placeholder) {
            opacity: 0.9;
        }
    }

    &__btn-wrapper {
        width: 100%;
        display: flex;
        flex-direction: column;

        @include media-breakpoint-up(lg) {
            flex-direction: row;
            justify-content: flex-end;
        }
    }

    &__close-wrapper {
        position: absolute;
        top: 18px;
        right: 12px;

        @include media-breakpoint-up(sm) {
            top: 12px;
            right: 12px;
        }
    }
}
</style>
